import type { MouseEvent } from 'react';

import type { ITableBody } from '../../../types/data_table';

import { Table } from '../../Table';
import { TableHeaderCell } from '../../Table/TableHeaderCell';
import { TableRow } from '../../Table/TableRow';

import { TableBodyCell } from './TableBodyCell';
import { TableBodyState } from './TableBodyState';

/** Cell types that own their clicks, so a click there is not a row click. */
const SELF_HANDLING_CELL_TYPES = ['action_btns', 'dropdown'];

/**
 * Renders the header row and the data rows of a `DataTable`, driven entirely
 * by `columnsMap`.
 *
 * It is presentational: it fetches nothing and holds no state. The container
 * decides whether to pass rows, `undefined` (still loading) or an error flag,
 * and this component picks between the rows and a {@link TableBodyState}.
 */
export const TableBody = ({
  lang = 'en',
  columnsMap,
  data,
  tableId,
  rowClickHandler,
  isLoading,
  isError,
  emptyState,
  ariaLabel,
}: ITableBody) => {
  /**
   * Row clicks are ignored when they land on a control that has its own
   * behaviour, so hitting an action button does not also open the row.
   */
  const handleRowClick = (
    event: MouseEvent<HTMLElement>,
    row: Record<string, any>
  ) => {
    const target = event.target as HTMLElement;

    const hasSelfHandlingCell = SELF_HANDLING_CELL_TYPES.some((type) =>
      target.closest(`[data-table-cell-type="${type}"]`)
    );

    if (!hasSelfHandlingCell) rowClickHandler?.(row);
  };

  /** The rows, or whichever of the three no-rows states applies. */
  const renderBody = () => {
    if (isLoading) {
      return (
        <TableBodyState status="loading" columnCount={columnsMap.length} />
      );
    }

    if (Array.isArray(data) && !data.length) {
      return <TableBodyState status="empty" emptyState={emptyState} />;
    }

    if (isError) return <TableBodyState status="error" lang={lang} />;

    /**
     * Rows are keyed by their own id when they have one. Falling back to the
     * array index makes React reuse a row's DOM for whatever row happens to
     * land in that slot next, which matters the moment a cell holds state.
     */
    const rowKey = (row: Record<string, any>, rowIndex: number) =>
      row?.id ?? row?.uuid ?? `row-${rowIndex}`;

    return data?.map((row: Record<string, any>, rowIndex: number) => (
      <TableRow
        // Only wired up when the consumer actually wants row clicks: a row
        // with a handler is put in the tab order, and a row that does nothing
        // must not be.
        onClickHandler={
          rowClickHandler ? (event) => handleRowClick(event, row) : undefined
        }
        key={rowKey(row, rowIndex)}
      >
        {columnsMap.map((column, columnIndex) => (
          <TableBodyCell
            key={`table-body-${column.id}`}
            column={column}
            row={row}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
            lang={lang}
          />
        ))}
      </TableRow>
    ));
  };

  return (
    <Table tableId={tableId} ariaLabel={ariaLabel} isLoading={isLoading}>
      <TableRow>
        {columnsMap?.map((column) => (
          <TableHeaderCell
            text={column.name}
            key={`table-header-cell-${column.id} `}
          />
        ))}
      </TableRow>

      {renderBody()}
    </Table>
  );
};
